/** @jsxImportSource @emotion/react */
import React, { useState, useCallback } from 'react'
import { css } from '@emotion/react'
import { Spin, App, Modal, Tooltip } from 'antd'
import {
  LoadingOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { font, weightMedium } from 'src/components/css'
import { ChevronDown, ChevronRight, ClockPlusIcon } from '../icons'
import type { SurfaceSectionProps, TimeSlotEditState } from './types'
import TimeSlotTableHeader from './TimeSlotTableHeader'
import TimeSlotRow from './TimeSlotRow'

const spinnerSmallStyle = { fontSize: 16 } as const

/**
 * Surface section component (collapsible surface with time slots table)
 */
const iconFontStyle = { fontSize: 18 } as const

const SurfaceSection: React.FC<SurfaceSectionProps> = ({
  facility,
  timeSlots,
  timeSlotCount,
  isExpanded,
  timeSlotsLoading,
  divisions,
  onToggle,
  onAddTimeSlot,
  onUpdateTimeSlot,
  onDeleteTimeSlot,
  onDuplicateTimeSlot,
}) => {
  const { message } = App.useApp()

  // Track which slot is being edited (null = none, 'new' = new row, slotId = existing)
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null)
  const [editState, setEditState] = useState<TimeSlotEditState | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)

  // Start adding a new slot
  const handleAddNewSlot = useCallback(() => {
    setEditingSlotId('new')
    setEditState({
      slotId: null,
      facilityId: facility.id,
      frequency: 'weekly',
      days: [1, 3, 5], // Mon, Wed, Fri default
      startTime: '09:00',
      endTime: '17:00',
      divisions: [],
    })
  }, [facility.id])

  // Start editing existing slot
  const handleEditSlot = useCallback(
    (slot: (typeof timeSlots)[0]) => {
      const timeRange = slot.time_ranges?.[0]
      setEditingSlotId(slot.id)
      setEditState({
        slotId: slot.id,
        facilityId: facility.id,
        frequency: slot.recurrence_type,
        days: slot.days || [],
        slotDate: slot.slot_date || null,
        startTime: timeRange?.start_time?.slice(0, 5) || '09:00',
        endTime: timeRange?.end_time?.slice(0, 5) || '17:00',
        divisions: slot.divisions || [],
      })
    },
    [facility.id]
  )

  // Save slot (create or update)
  const handleSave = useCallback(async () => {
    if (!editState) {
      return
    }

    setSaveLoading(true)
    try {
      const data = {
        label: `Time Slot ${timeSlots.length + 1}`,
        recurrence_type: editState.frequency,
        days: editState.frequency === 'specific' ? [] : editState.days,
        time_ranges: [
          {
            start_time: editState.startTime,
            end_time: editState.endTime,
          },
        ],
        divisions: editState.divisions,
        slot_date:
          editState.frequency === 'specific'
            ? editState.slotDate || null
            : null,
      }

      if (editState.slotId) {
        // Update existing
        await onUpdateTimeSlot(editState.slotId, data)
        message.success('Time slot updated')
      } else {
        // Create new
        await onAddTimeSlot(facility.id, data)
        message.success('Time slot created')
      }

      setEditingSlotId(null)
      setEditState(null)
    } catch {
      message.error('Failed to save time slot')
    } finally {
      setSaveLoading(false)
    }
  }, [
    editState,
    facility.id,
    onAddTimeSlot,
    onUpdateTimeSlot,
    timeSlots.length,
    message,
  ])

  // Cancel editing
  const handleCancel = useCallback(() => {
    setEditingSlotId(null)
    setEditState(null)
  }, [])

  // Delete all time slots for this surface
  const handleDeleteAllTimeSlots = useCallback(() => {
    if (timeSlots.length === 0) {
      return
    }
    Modal.confirm({
      title: 'Delete All Time Slots',
      content: `Are you sure you want to delete all ${
        timeSlots.length
      } time slot${timeSlots.length !== 1 ? 's' : ''} for ${facility.name}?`,
      okText: 'Delete All',
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          await Promise.all(timeSlots.map((slot) => onDeleteTimeSlot(slot.id)))
          message.success(`All time slots deleted from ${facility.name}`)
        } catch {
          message.error('Failed to delete some time slots')
        }
      },
    })
  }, [timeSlots, facility.name, onDeleteTimeSlot, message])

  // Delete slot
  const handleDelete = useCallback(
    (slotId: string) => {
      Modal.confirm({
        title: 'Delete Time Slot',
        content: 'Are you sure you want to delete this time slot?',
        okText: 'Delete',
        okButtonProps: { danger: true },
        centered: true,
        onOk: async () => {
          try {
            await onDeleteTimeSlot(slotId)
            message.success('Time slot deleted')
          } catch {
            message.error('Failed to delete time slot')
          }
        },
      })
    },
    [onDeleteTimeSlot, message]
  )

  return (
    <div>
      {/* Surface Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          background-color: ${colors.SECONDARY_400};
          width: 100%;
          cursor: pointer;
          user-select: none;

          &:hover {
            background-color: #1e2733;
          }
        `}
        onClick={onToggle}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        <div
          css={css`
            display: flex;
            flex: 1 0 0;
            align-items: center;
            gap: 8px;
            max-width: 600px;
          `}
        >
          <div
            css={css`
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            `}
          >
            {isExpanded ? (
              <ChevronDown size={24} color={colors.NEUTRAL_50} />
            ) : (
              <ChevronRight size={24} color={colors.NEUTRAL_50} />
            )}
          </div>

          <span
            css={css`
              flex: 1 0 0;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 500;
              line-height: 20.3px;
              color: ${colors.NEUTRAL_0};
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {facility.name}
          </span>

          <span
            css={css`
              width: 265px;
              flex-shrink: 0;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 400;
              line-height: 20.3px;
              color: ${colors.NEUTRAL_50};
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {timeSlotCount} Time Slot{timeSlotCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Action buttons */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: flex-end;
            width: 120px;
            flex-shrink: 0;
          `}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title='Add Time Slot' placement='top'>
            <button
              type='button'
              onClick={handleAddNewSlot}
              disabled={editingSlotId === 'new'}
              css={css`
                background: transparent;
                border: none;
                color: ${colors.PRIMARY_300};
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;

                &:hover {
                  color: ${colors.PRIMARY_200};
                }

                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
              aria-label={`Add time slot to ${facility.name}`}
            >
              <ClockPlusIcon size={24} color={colors.PRIMARY_300} />
            </button>
          </Tooltip>
          <Tooltip title='Delete All Time Slots' placement='top'>
            <button
              type='button'
              onClick={handleDeleteAllTimeSlots}
              disabled={timeSlots.length === 0}
              css={css`
                background: transparent;
                border: none;
                color: ${colors.ERROR_200};
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;

                &:hover {
                  color: ${colors.ERROR_300};
                }

                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
              aria-label={`Delete all time slots for ${facility.name}`}
            >
              <DeleteOutlined style={iconFontStyle} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Time Slots Content */}
      {isExpanded && (
        <div
          css={css`
            background-color: ${colors.SECONDARY_500};
          `}
        >
          {/* Table Header */}
          <TimeSlotTableHeader />

          {/* Loading state */}
          {timeSlotsLoading && (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 12px;
              `}
            >
              <Spin
                indicator={<LoadingOutlined style={spinnerSmallStyle} spin />}
              />
              <span
                css={css`
                  margin-left: 8px;
                  color: ${colors.NEUTRAL_100};
                  font-size: 13px;
                `}
              >
                Loading time slots...
              </span>
            </div>
          )}

          {/* Existing time slots */}
          {!timeSlotsLoading &&
            timeSlots.map((slot, index) => (
              <TimeSlotRow
                key={slot.id}
                timeSlot={slot}
                index={index}
                isEditing={editingSlotId === slot.id}
                editState={editingSlotId === slot.id ? editState : null}
                divisions={divisions}
                onEdit={() => handleEditSlot(slot)}
                onSave={handleSave}
                onCancel={handleCancel}
                onDelete={() => handleDelete(slot.id)}
                onDuplicate={() => onDuplicateTimeSlot(slot.id)}
                onEditStateChange={setEditState}
                loading={saveLoading}
              />
            ))}

          {/* New slot row (when adding) — at bottom of list */}
          {editingSlotId === 'new' && editState && (
            <TimeSlotRow
              timeSlot={{
                id: 'new',
                label: 'New Time Slot',
                recurrence_type: editState.frequency,
                is_active: true,
                days: editState.days,
                time_ranges: [
                  {
                    id: 'new',
                    start_time: editState.startTime,
                    end_time: editState.endTime,
                    sort_order: 0,
                  },
                ],
                facility_id: facility.id,
                schedule_id: '',
              }}
              index={timeSlots.length}
              isEditing={true}
              editState={editState}
              divisions={divisions}
              onEdit={() => {}}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={() => handleCancel()}
              onEditStateChange={setEditState}
              loading={saveLoading}
            />
          )}

          {/* Empty state */}
          {!timeSlotsLoading &&
            timeSlots.length === 0 &&
            editingSlotId !== 'new' && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                  padding: 10px 16px;
                  background-color: ${colors.SECONDARY_500};
                `}
              >
                <button
                  type='button'
                  onClick={handleAddNewSlot}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    background: transparent;
                    border: 1px solid ${colors.PRIMARY};
                    color: ${colors.PRIMARY};
                    padding: 2px 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    ${font.body}
                    font-size: 12px;
                    ${weightMedium}

                    &:hover {
                      background: ${colors.PRIMARY}0D;
                    }
                  `}
                  aria-label={`Add time slot to ${facility.name}`}
                >
                  <PlusOutlined
                    css={css`
                      font-size: 10px;
                    `}
                  />
                  Add Time Slot
                </button>
              </div>
            )}

          {/* Add Time Slot button at bottom (when time slots exist) */}
          {editingSlotId !== 'new' && timeSlots.length > 0 && (
            <div
              css={css`
                padding: 4px 12px;
                display: flex;
                justify-content: center;
              `}
            >
              <button
                type='button'
                onClick={handleAddNewSlot}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  padding: 2px 8px;
                  background: transparent;
                  border: 1px solid ${colors.PRIMARY_300};
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s ease;

                  &:hover {
                    background: ${colors.PRIMARY_300}20;
                  }
                `}
                aria-label={`Add time slot to ${facility.name}`}
              >
                <PlusOutlined
                  css={css`
                    font-size: 12px;
                    color: ${colors.PRIMARY_300};
                  `}
                />
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 17.4px;
                    color: ${colors.PRIMARY_300};
                  `}
                >
                  Add Time Slot
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SurfaceSection
